// 電子簽核 — 前端資料層(報帳)。RFC-approval-workflows.md Phase 3。
import { useQuery } from '@tanstack/react-query';
import { type ApiError, type DevActorPreset, api } from './api.ts';
import type { BillingCycle, SigningStatus } from './use-contracts.ts'; // type-only → 執行期沒有相依

export type ApprovalStatus =
  | 'draft'
  | 'submitted'
  | 'approved'
  | 'paid'
  | 'rejected'
  | 'returned'
  | 'withdrawn'
  | 'cancelled';
import type { RemitItem } from './statutory-remit.ts';

export interface ApprovalListRow {
  id: string;
  request_no: string | null;
  request_type:
    | 'expense'
    | 'payment'
    | 'leave'
    | 'advisory'
    // V417 管理費開票（一期 call 一張單、逐位 LP 回填統一發票號碼）
    | 'fee_invoice'
    | 'contract'
    | 'investment'
    | 'trip' // V384 出差申請
    | 'shareholder_loan' // V490 股東往來
    | 'vendor_request' // V394 申請建立廠商
    // V399 / V402 投資承諾與請款（從專案頁的承諾列送審）
    | 'commitment'
    | 'commitment_disbursement'
    // V348 營運事件 gate（從基金/專案頁送審）
    | 'capital_call'
    | 'distribution'
    | 'fund_valuation'
    | 'project_valuation'
    // V349 批 3b 從 workflow 搬過來的
    | 'fund_status_change'
    | 'project_status_change'
    | 'party_status_change'
    | 'access_request'
    | 'internal_signup_review';
  title: string;
  amount: string | null;
  currency: string;
  status: ApprovalStatus;
  current_step_seq: number | null;
  /**
   * 這張單還有沒有「等人去做」的執行關（出納付款 / 財務主管開票）。
   *
   * 2026-09-12 使用者:「已核准 應該是在已結案中」—— `approved` 本身不代表還沒結束:
   * 核准即終態的型別 (Capital Call 發放 / 投資核決 / 狀態變更…) 一核准就沒事了,
   * 只有卡在執行關的才真的還在進行中。 後端 inbox 判「輪到出納 / 開票」用的就是
   * 這個條件, 清單直接吃同一個事實, 不要在前端猜型別白名單 (猜錯就是說謊)。
   */
  pending_execute?: boolean;
  /**
   * 現在輪到誰 —— 2026-09-12 使用者:「這一頁 我還要看到 現在在哪一關」。
   * 狀態藥丸只到「待簽核」, 講不出卡在誰那裡; 送出的人想知道的是「還要等誰」。
   * 待簽核 → 目前那一關; 已核准但還有執行關 → 那一關。 都沒有就是 null。
   */
  current_step_label?: string | null;
  /**
   * 這一關**實際上在誰身上**（V437 解析）—— 角色 / 職級關也會解析成人名，
   * 因為「財務主管」答的是哪一種人、不是要去催誰。 可能多人（都有資格簽）。
   */
  current_step_approvers?: string[] | null;
  /** 這一關從什麼時候開始等（上一關簽掉的時間，沒有上一關就是送出時間）。 */
  current_step_since?: string | null;
  submitted_at: string | null;
  created_at: string;
  /**
   * 申請日期 (台北)。 2026-09-12 使用者:「流程 都只是申請日」。
   *
   * ⚠ 不要自己用 `submitted_at.slice(0,10)` 算 —— 那是 UTC 的日子, 台北 0~8 點會
   *   少一天 (正式站 48 張單有 14 張會錯)。 後端已經轉好, 直接用。
   * ⚠ 草稿一律 null: 被「收回修改」的草稿身上還留著上次的 submitted_at,
   *   它現在不在申請中。
   */
  applied_on: string | null;
  requester_name: string;
  /** 成本歸屬記帳主體的簡稱 (V509 short_name, 沒設退回全名)。 請假沒有這個概念 → null。 */
  gp_company_name: string | null;
  /** 記帳主體全名 —— 清單的滑鼠提示。 */
  gp_company_full_name?: string | null;
  /** 借款單 (shareholder_loan) 的方向; 其他型別 null。 */
  loan_direction?: 'borrow' | 'repay' | null;
  /**
   * V373: 這張單是排程產生並自動送審的 (目前只有合約週期性付款)。
   * requester_id 仍是真人 (權限/RLS 需要), 但畫面上不該叫他「申請人」—— 他沒有申請。
   */
  created_by_system?: boolean;
  /** 來源合約的簽核單號 (CON-…)。 有值時申請人顯示成「系統（依合約 CON-…）」。 */
  source_contract_no?: string | null;
  /** V384: 報帳掛了哪趟出差 (approval_request.id)。 清單據此在摘要前加 ✈️。 */
  trip_request_id?: string | null;
}

/**
 * 「申請人」顯示名 (V373)。
 *
 * 合約週期性付款是排程產生並自動送審的, requester_id 掛在合約建立人身上 (權限 /
 * RLS / 「我送出的」都要一個真人), 但把他寫成「申請人」是**說謊** —— 他沒有申請。
 * 金融系統的稽核軌跡不該說謊, 而且日後查「這筆錢為什麼付出去」時, 指回合約比指到
 * 一個人有用。
 */
export function requesterLabel(r: {
  requester_name: string;
  created_by_system?: boolean;
  source_contract_no?: string | null;
}): string {
  if (!r.created_by_system) return r.requester_name;
  return r.source_contract_no ? `系統（依合約 ${r.source_contract_no}）` : '系統';
}

export interface ApprovalStep {
  seq: number;
  label: string | null;
  // P2-b: 加 resource_manager(submit 已解析成 user/title_rank) + title_rank。
  // V347: supervisor(直屬主管，submit 已解析成 user)。
  approver_kind:
    | 'role'
    | 'user'
    | 'resource_manager'
    | 'resource_sponsor'
    | 'title_rank'
    | 'supervisor';
  approver_role: string | null;
  approver_user_id: string | null;
  approver_name: string | null;
  approver_title_rank: number | null;
  // V347: 'notify'＝知會關 — 不擋流程，到達即 'notified' + 進鈴鐺。
  step_type: StepType;
  /** 指定簽核人是否仍在職。false = 這關卡死了（只有本人/代理能簽），要改派。 */
  approver_is_active: boolean | null;
  /**
   * 這一關現在誰簽得動（只有待簽的審核 / 執行關才算，V437）。
   * 已簽掉的關不算 —— 那一行要顯示的是**實際簽的人**（可能是職務代理）。
   */
  eligible_names?: string[] | null;
  /** V389: 這一關的簽核人能不能當場修改內容 / 退回上一關。 */
  can_edit: boolean;
  can_return_prev: boolean;
  status: 'pending' | 'approved' | 'rejected' | 'skipped' | 'notified';
  acted_at: string | null;
  comment: string | null;
  acted_by_name: string | null;
  /** V381: 這一關由**送單人本人**簽掉, 且當時還有其他合格簽核人 —— 一般會被防呆
   *  擋下, 執行合夥人才放行。 時間軸上要標出來 (使用者要的「留痕」)。 */
  self_approved: boolean | null;
}

// 簽核人的職級門檻 / 角色 → 中文。 簽核路線設定頁 (PolicyInspector) 與簽核單進度
// (stepApproverLabel) 共用這一份 —— 原本兩邊各一套, 同一關在設定頁叫「執行合夥人以上」、
// 單子上叫「Managing Partner」。
export const APPROVER_RANK_LABEL: Record<number, string> = {
  100: '執行合夥人以上',
  90: '合夥人以上',
  70: '董事以上',
  60: 'VP 以上',
};
export const APPROVER_ROLE_LABEL: Record<string, string> = {
  owner: '負責人',
  admin: '管理員',
  finance: '財務主管',
  cashier: '出納',
  hr: '人資主管',
  investment: '投資主管',
  invoicing: '開票',
  resource_manager: '該基金/專案負責人',
  managing_partner: '執行合夥人',
};

// P2-b: 關卡審核人的人看得懂說法（role code / title rank → 中文）。
export function stepApproverLabel(s: ApprovalStep): string {
  if (s.approver_kind === 'user') return s.approver_name ?? '指定簽核人';
  if (s.approver_kind === 'title_rank') {
    const rank = s.approver_title_rank ?? 0;
    return APPROVER_RANK_LABEL[rank] ?? `職級 ≥ ${rank}`;
  }
  return s.approver_role ? (APPROVER_ROLE_LABEL[s.approver_role] ?? s.approver_role) : '簽核人';
}

export interface ApprovalLine {
  line_no: number;
  expense_date: string;
  category: string;
  amount: string;
  currency: string;
  fx_rate: string | null;
  amount_twd: string | null;
  /** V461 可扣抵進項稅額 (0 = 不拆) */
  input_tax_amount: string | null;
  // V314: 費用歸屬移到表頭 (ApprovalDetail.exp_charge_*) — 明細只剩這些
  note: string | null;
}

/** V389: 一次內容修改。 changes 已在後端格式化成人看得懂的字串。 */
export interface ApprovalEdit {
  id: string;
  edited_at: string;
  /** null = 申請人在草稿 / 被退回時自己改的（不是簽核途中被動過）。 */
  at_step_seq: number | null;
  at_step_label: string | null;
  changes: string;
  /**
   * V486: edit = 內容修改；return = 退回補件 / 退回上一關；
   * event = 建立、送出、核准、駁回、付款、附件… (DB trigger 寫的)。
   */
  kind: 'edit' | 'return' | 'event';
  editor_name: string | null;
}

/** 合約詳情列出的「依這份合約開的服務發票」一列 (detail endpoint 的 con_invoices)。 */
export interface ContractInvoice {
  request_id: string;
  invoice_no: string | null;
  title: string | null;
  service_start: string | null;
  service_end: string | null;
  status: string;
  gross: string;
  currency: string;
  collected_amount: string;
}

export interface ApprovalDetail extends Omit<ApprovalListRow, 'submitted_at'> {
  submitted_at: string | null;
  /** 申請人 email — 前端用來判斷「這是不是我送的單」（只影響顯示；後端 guardEditable 把關）。 */
  requester_email: string | null;
  /** V373: 排程產生 + 自動送審的單 (合約週期性付款)。 見 requesterLabel()。 */
  created_by_system?: boolean;
  source_contract_no?: string | null;
  /**
   * 2026-08-17: 目前這一關輪不輪得到我 —— 由後端用**跟 act 端點同一支**
   * canActOnStep 算出來。 前端自己算不了: 能力型關卡要查 user_is_finance() 之類,
   * 指定人關卡還要查職務代理。 沒有它就只能把核准鍵畫給每個人, 按下去才 403。
   */
  can_act_current?: boolean;
  /** V381: 記付款這一步輪不輪得到我 —— 含自我簽核防呆 (前端算不了)。 */
  can_mark_paid?: boolean;
  /** V490: 股東往來的「記到款」輪不輪得到我 (後端跟 settle 端點同一個判斷)。 */
  can_settle_loan?: boolean;
  /** 目前待簽關卡的名稱 (輪不到我時, 用來說「現在在誰那一關」)。 */
  current_step_label?: string | null;
  decided_at: string | null;
  paid_at: string | null;
  payment_note: string | null;
  /**
   * 付款日（台北）。 **不要用 paid_at.slice(0,10)** —— 那是 UTC，台北零時會少一天。
   */
  paid_on: string | null;
  /** V390 匯款手續費 —— 出納記付款時填。 */
  bank_fee: string | null;
  /** 誰負擔：us = 我方付（記入「手續費」科目）／payee = 對方付（不進我們的帳）。 */
  bank_fee_bearer: 'us' | 'payee' | null;
  // V348 營運事件 gate：entity 綁定 + payload（capital_call/distribution/valuation）
  entity_type: 'fund' | 'project' | null;
  entity_id: string | null;
  payload: Record<string, unknown> | null;
  /** 編輯模式回填用（reimburse_to_name 只是顯示用 label, 反查會漂 — 同 charge code）。 */
  exp_reimburse_to_user_id: string | null;
  reimburse_to_name: string | null;
  claim_note: string | null;
  // 請假欄位(request_type='leave' 時有值)
  leave_type: string | null;
  leave_start: string | null;
  leave_end: string | null;
  leave_start_time: string | null;
  leave_end_time: string | null;
  leave_days: string | null;
  leave_reason: string | null;
  deputy_name: string | null;
  // 付款欄位(request_type='payment' 時有值)
  payee_name: string | null;
  payee_bank_account: string | null;
  /** 主檔上現在的帳號 (廠商銀行欄 / 集團內主體的主要帳戶)。 快照空白時才拿來顯示。 */
  payee_bank_account_master?: string | null;
  pay_invoice_no: string | null;
  pay_category: string | null;
  /** V433 申請人寫的備註 (同報帳的 claim_note)。 */
  pay_note: string | null;
  // 編輯模式回填 charge code 用（label 是顯示用，反查會漂）
  pay_charge_type: 'management_company' | 'fund' | 'project' | null;
  pay_charge_ref_id: string | null;
  vendor_id: string | null;
  vendor_name: string | null;
  /** V415: 收款對象是集團內的記帳主體時 (與 vendor_id 互斥)。 */
  payee_ledger_entity_id: string | null;
  payee_entity_name: string | null;
  // V312 就源扣繳 — 實付 = amount − withholding − supp_nhi
  pay_income_category: string | null;
  pay_withholding_rate: string | null;
  pay_withholding_amount: string | null;
  /** V379 可扣抵進項稅額 (0 = 不拆, 費用認列全額)。 */
  pay_input_tax_amount: string | null;
  pay_supp_nhi_amount: string | null;
  /** V493 付款科目「勞健保勞退繳納」時其中員工自付 (沖 2310); 其他科目 '0.00'。 */
  pay_employee_share_amount: string | null;
  /** V501 法定繳款的所屬月份 (月初) 與項目; 一般付款都是 null。 */
  pay_remit_period: string | null;
  pay_remit_item: RemitItem | null;
  /** V432 扣下來的稅實際繳給國稅局那天; null = 還掛在 2330 沒沖銷。 */
  pay_withholding_remitted_date: string | null;
  pay_due_date: string | null;
  pay_charge_label: string | null;
  // V315: 付款公司 (GP)
  pay_gp_company_id: string | null;
  pay_gp_name: string | null;
  // V314 報帳表頭: 費用歸屬 + 付款公司 (GP)
  exp_charge_type: 'management_company' | 'fund' | 'project' | null;
  exp_charge_ref_id: string | null;
  exp_charge_label: string | null;
  exp_gp_company_id: string | null;
  exp_gp_name: string | null;
  pay_source_contract_id: string | null; // 來自合約自動生成 (Phase 2) 時有值
  pay_source_contract_name: string | null;
  // 顧問費欄位(request_type='advisory' 時有值)
  adv_invoice_no: string | null;
  adv_client_type: 'project' | 'party' | 'external' | null;
  adv_client_name: string | null;
  adv_client_tax_id: string | null;
  adv_service_item: string | null;
  adv_net_amount: string | null;
  adv_tax_rate: string | null;
  adv_issue_date: string | null;
  adv_service_start: string | null;
  adv_service_end: string | null;
  adv_external_no: string | null;
  adv_notes: string | null;
  // V394 申請建立廠商
  vr_name: string | null;
  vr_tax_id: string | null;
  vr_bank_name: string | null;
  vr_branch_name: string | null;
  vr_bank_account: string | null;
  vr_account_holder: string | null;
  vr_contact_name: string | null;
  vr_contact_phone: string | null;
  vr_email: string | null;
  vr_address: string | null;
  vr_withholding_rate: string | null;
  vr_expense_category: string | null;
  vr_note: string | null;
  vr_source_request_id: string | null;
  vr_source_request_no: string | null;
  vr_created_vendor_id: string | null;
  adv_status: string | null;
  adv_gp_name: string | null;
  // 退回後編輯要用的原始 id / 值（顯示欄位是 join 出來的名字，反查會在改名後漂）。
  /** advisory_invoice.title（服務摘要）— 不是帶前綴的 approval_request.title。 */
  adv_title: string | null;
  adv_gp_company_id: string | null;
  adv_client_project_id: string | null;
  adv_client_party_id: string | null;
  adv_currency: string | null;
  adv_revenue_account_code: string | null;
  /** 2026-10-27 依據合約 —— 連回那份合約的簽核單。 */
  adv_source_contract_id?: string | null;
  adv_source_contract_name?: string | null;
  adv_source_contract_request_id?: string | null;
  adv_source_contract_request_no?: string | null;
  // 合約 / 訂閱欄位(request_type='contract' 時有值)
  /** 依這份合約開的服務發票 (各期)。 不是合約的單 = null。 */
  con_invoices?: ContractInvoice[] | null;
  con_name: string | null;
  con_amount: string | null; // 每期金額
  con_currency: string | null;
  con_billing_cycle: BillingCycle | null;
  con_category: string | null;
  con_start_date: string | null;
  con_end_date: string | null;
  con_next_renewal_date: string | null;
  con_auto_renew: boolean | null;
  /** V418: FALSE = 每期不自動生付款草稿（排程與到期提醒照常）。 */
  con_auto_generate_payment: boolean | null;
  /** V427: 每期金額是含稅 (預設) 還是未稅。 */
  con_amount_is_tax_inclusive: boolean | null;
  /** V428: 不排程自動生, 但允許人指定期別手動產生。 */
  con_allow_manual_generate: boolean | null;
  con_notice_days: number | null;
  /** 合約備註 —— 事後議定的事實 (例如「8 月免租、9 月起算」) 放這裡。 */
  con_note: string | null;
  /** 下期付款日 —— 週期性付款草稿的排程起點。 */
  con_next_payment_date: string | null;
  /** V506: 對方（夥伴或打字）與簽署狀態。 */
  con_counterparty_party_id?: string | null;
  con_counterparty_name?: string | null;
  con_signing_status?: SigningStatus | null;
  con_signed_date?: string | null;
  con_status: string | null;
  con_vendor_name: string | null;
  con_gp_name: string | null;
  /** 編輯模式回填用的原始 id (名稱是顯示用, 反查會漂)。 */
  con_vendor_id: string | null;
  con_gp_company_id: string | null;
  con_payee_ledger_entity_id: string | null;
  /** V415 的集團內對象名稱 —— 沒有這個, 詳情頁的「對象」對集團內往來永遠是「—」。 */
  con_payee_entity_name: string | null;
  con_charge_label: string | null;
  con_charge_type: 'management_company' | 'fund' | 'project' | null;
  con_charge_ref_id: string | null;
  con_annualized: string | null;
  // 投資核決欄位(request_type='investment' 時有值) — V329
  inv_source_gp_name: string | null;
  inv_source_gp_company_id: string | null;
  inv_target_kind: 'fund' | 'gp_company' | 'external' | 'party' | null;
  inv_instrument: 'equity' | 'debt' | null;
  inv_amount: string | null;
  inv_currency: string | null;
  inv_ownership_pct: string | null;
  inv_interest_rate: string | null;
  inv_maturity_date: string | null;
  inv_expected_date: string | null;
  inv_note: string | null;
  inv_target_fund_name: string | null;
  inv_target_gp_name: string | null;
  inv_target_party_name: string | null;
  inv_target_name: string | null;
  inv_target_fund_id: string | null;
  inv_target_gp_id: string | null;
  inv_target_party_id: string | null;
  // V490 股東往來 (request_type='shareholder_loan' 時有值)
  shl_gp_company_id?: string | null;
  shl_gp_name?: string | null;
  shl_entity_kind?: 'gp' | 'fund' | null;
  shl_direction?: 'borrow' | 'repay' | null;
  shl_lender_party_id?: string | null;
  shl_lender_entity_id?: string | null;
  shl_lender_name?: string | null;
  shl_amount?: string | null;
  shl_currency?: string | null;
  shl_expected_date?: string | null;
  shl_maturity_date?: string | null;
  shl_lender_is_shareholder?: boolean | null;
  shl_note?: string | null;
  shl_settled_date?: string | null;
  /** V507 出借方 (集團內記帳主體) 的撥付單 —— 最新一張 */
  shl_lender_payment_id?: string | null;
  shl_lender_payment_no?: string | null;
  shl_lender_payment_status?: ApprovalStatus | null;
  /** V507 這張付款是哪張借款的撥付 */
  pr_source_loan_id?: string | null;
  pr_source_loan_no?: string | null;
  // V384 出差申請 (request_type='trip' 時有值)
  trip_country: string | null;
  trip_city: string | null;
  trip_start: string | null;
  trip_end: string | null;
  trip_purpose: string | null;
  trip_local_currency: string | null;
  trip_charge_type: 'management_company' | 'fund' | 'project' | null;
  trip_charge_ref_id: string | null;
  trip_charge_label: string | null;
  trip_gp_company_id: string | null;
  trip_gp_name: string | null;
  trip_deputy_user_id: string | null;
  trip_deputy_name: string | null;
  trip_note: string | null;
  trip_itinerary: TripItineraryRow[] | null;
  trip_estimates: TripEstimateRow[] | null;
  // V384 報帳 → 出差聯結 (request_type='expense' 時可能有值)
  exp_trip_request_id: string | null;
  exp_trip_request_no: string | null;
  steps: ApprovalStep[];
  lines: ApprovalLine[];
  /** V389 內容修改軌跡 —— 誰在哪一關把什麼改成什麼。 */
  edits: ApprovalEdit[];
}

// ---- V384 出差 ----
export interface TripItineraryRow {
  item_date: string;
  activity: string;
  location: string | null;
}
export interface TripEstimateRow {
  category: string;
  amount: string;
  currency: string;
  fx_rate: string | null;
  amount_twd: string;
}
/** 報帳表單「關聯出差申請」下拉的一列。 */
export interface LinkableTrip {
  id: string;
  request_no: string | null;
  status: ApprovalStatus;
  title: string;
  destination_country: string;
  destination_city: string | null;
  start_date: string;
  end_date: string;
  purpose: string;
  charge_type: 'management_company' | 'fund' | 'project';
  charge_ref_id: string | null;
  charge_label: string | null;
  gp_company_id: string | null;
  local_currency: string;
  /** 預估填過的匯率 {JPY: '0.21', …} — 報帳外幣列旁當提示, 不自動填。 */
  estimate_fx: Record<string, string>;
  /**
   * 這趟已經被哪些報帳單掛了 (含草稿/退回)。
   *
   * 「一張出差對一張報帳」是**預設不是規則** —— 挑到已被報過的出差時提示但不擋,
   * 也沒有 unique 約束。 理由: 補報 (原單已核准改不了) 只剩「另開一張掛同一趟」
   * 或「開一張不掛出差」, 擋住等於逼人走後者, 剛好把最需要佐證的那筆關聯弄丟。
   */
  linked_claims: { request_no: string | null; status: ApprovalStatus }[];
}
/**
 * 報帳表單「關聯出差申請」的候選清單。
 *
 * 2026-09-12: 加 forUserId —— 出差申請的申請人就是報帳的受款人, 代別人報帳時要列
 * **受款人**的行程, 不是登入者自己的 (原本寫死自己, 代報帳等於關聯不到出差)。
 * 空 / 自己 → 不帶參數, 行為不變; 調別人的行程後端只放行人資 / 財務 / 系統管理員,
 * 其餘回 403 (呼叫端把訊息顯示在下拉底下, 不當成整頁錯誤)。
 */
export function useLinkableTrips(
  actor: DevActorPreset,
  includeId?: string | null,
  forUserId?: string | null,
) {
  return useQuery<LinkableTrip[], ApiError>({
    queryKey: ['trips-linkable', actor, includeId ?? null, forUserId ?? null],
    queryFn: () => {
      const qs = new URLSearchParams();
      if (includeId) qs.set('include_id', includeId);
      if (forUserId) qs.set('for_user_id', forUserId);
      const q = qs.toString();
      return api<LinkableTrip[]>(`/api/v1/approvals/trips/linkable${q ? `?${q}` : ''}`, { actor });
    },
    retry: false, // 403 (沒權限調別人的行程) 不用重試
  });
}
export interface TripSummary {
  linked_claims: {
    id: string;
    request_no: string | null;
    title: string;
    status: ApprovalStatus;
    amount: string | null;
    submitted_at: string | null;
  }[];
  actuals: { category: string; amount_twd: string }[];
  estimates: TripEstimateRow[];
}
export function useTripSummary(id: string | undefined, actor: DevActorPreset, enabled: boolean) {
  return useQuery<TripSummary, ApiError>({
    queryKey: ['trip-summary', id, actor],
    queryFn: () => api<TripSummary>(`/api/v1/approvals/${id}/trip-summary`, { actor }),
    enabled: !!id && enabled,
  });
}

export interface StaffMember {
  id: string;
  name: string;
  /** V371: 職級名稱 (執行合夥人 / 合夥人 / …)。 沒指派職級 → null。 */
  title?: string | null;
}

/**
 * 成員選單的顯示文字 —— 「姚舜晏（執行合夥人）」。
 *
 * V371 把清單改成**職級由高到低**排序 (原本是姓名, 但 en_US 定序對中文等於
 * Unicode 碼位 = 部首序, 沒有人讀得出規律)。 ⚠ 只改順序而不顯示依據, 使用者只會
 * 看到名字換了位置卻不知道為什麼 —— 那比原本更像隨機, 所以職級一定要跟著顯示。
 */
export function staffLabel(s: StaffMember): string {
  return s.title ? `${s.name}（${s.title}）` : s.name;
}
export interface LeaveSummary {
  year: number;
  used: { leave_type: string; used_days: string }[];
  entitled: {
    leave_type: string;
    entitled_days: string;
    statutory_days: string;
    welfare_days: string;
  }[];
  hours_per_day: number;
  /** V316 特休拆分 —— 法定(§38, 未休須折算) / 福利(公司給, 年底歸零)。扣假法定優先。 */
  annual_split: {
    statutory: number;
    welfare: number;
    statutory_used: number;
    welfare_used: number;
    /** 年底 / 離職時要折算工資的天數 —— 只有法定那桶算 */
    statutory_remaining: number;
    welfare_remaining: number;
  } | null;
}

export interface LeaveType {
  code: string;
  name_zh: string;
  name_en: string;
  unit: 'day' | 'half_day' | 'hour';
  is_paid: boolean;
  pay_rate: string;
  annual_cap_days: string | null;
  deduct_from_balance: boolean;
  carry_over: boolean;
  statutory_basis: string | null;
}

export interface ChargeCodes {
  management_company: { label: string }[];
  funds: { id: string; name: string; code: string }[];
  projects: { id: string; name: string; code: string }[];
}

export type ApprovalBox = 'inbox' | 'sent' | 'all';

export function useApprovalsList(box: ApprovalBox, actor: DevActorPreset) {
  return useQuery<ApprovalListRow[], ApiError>({
    queryKey: ['approvals', box, actor],
    queryFn: () => api<ApprovalListRow[]>(`/api/v1/approvals?box=${box}`, { actor }),
    // 同 workflow bell cadence — inbox 也餵 sidebar/bell 待辦數。
    // 2026-08-01 效能：30s → 60s，改用回到分頁即刷新（見 use-workflows.ts 說明）。
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    staleTime: 10_000,
  });
}

export function useApprovalDetail(id: string | undefined, actor: DevActorPreset) {
  return useQuery<ApprovalDetail, ApiError>({
    queryKey: ['approval', id, actor],
    queryFn: () => api<ApprovalDetail>(`/api/v1/approvals/${id}`, { actor }),
    enabled: !!id,
  });
}

export interface ApprovalAttachment {
  id: string;
  filename: string;
  content_type: string | null;
  size_bytes: string | null;
  uploaded_by: string | null;
  uploader_name: string | null;
  created_at: string;
  download_url: string;
}

export function useAttachments(id: string | undefined, actor: DevActorPreset) {
  return useQuery<ApprovalAttachment[], ApiError>({
    queryKey: ['approval-attachments', id, actor],
    queryFn: () => api<ApprovalAttachment[]>(`/api/v1/approvals/${id}/attachments`, { actor }),
    enabled: !!id,
  });
}

export function useChargeCodes(actor: DevActorPreset) {
  return useQuery<ChargeCodes, ApiError>({
    queryKey: ['charge-codes', actor],
    queryFn: () => api<ChargeCodes>('/api/v1/charge-codes', { actor }),
  });
}

export function useStaff(actor: DevActorPreset) {
  return useQuery<StaffMember[], ApiError>({
    queryKey: ['approval-staff', actor],
    queryFn: () => api<StaffMember[]>('/api/v1/approval-staff', { actor }),
  });
}

// ---- 職務代理 (approval_delegation) — T5 ----
export interface Delegation {
  id: string;
  delegator_user_id: string;
  delegate_user_id: string;
  delegator_name: string | null;
  delegate_name: string | null;
  start_date: string;
  end_date: string;
  note: string | null;
  revoked_at: string | null;
  created_at: string;
  active: boolean;
}
export function useDelegations(actor: DevActorPreset) {
  return useQuery<Delegation[], ApiError>({
    queryKey: ['approval-delegations', actor],
    queryFn: () => api<Delegation[]>('/api/v1/approval-delegations', { actor }),
  });
}

// ---- 簽核路線檢視 (T8 唯讀) ----
/** V350 execute（核准後要有人做）/ V357 auto（免審核，送出即生效）。 */
export type StepType = 'approve' | 'notify' | 'execute' | 'auto';

export interface ApprovalPolicyRow {
  id: string;
  request_type: string;
  seq: number;
  label: string | null;
  min_amount: string | null;
  approver_kind: string;
  approver_role: string | null;
  approver_user_id: string | null;
  approver_user_name: string | null;
  approver_title_rank: number | null;
  step_type: StepType;
  active: boolean;
  /** V389: 只有簽核關能開；執行 / 知會 / 免審核關恆為 false（DB CHECK 保證）。 */
  can_edit: boolean;
  can_return_prev: boolean;
  /** V471: 這一關什麼時候出現 —— always / with_resource（單子掛了基金或專案）/
   *  without_resource（營運費用）。 報帳請款靠它做到「順序隨情況換」。 */
  applies_when: 'always' | 'with_resource' | 'without_resource' | 'bound_project' | 'bound_fund';
  /** V514: 解析出來就是送單人本人時照樣保留這一關（不套「自己送的單跳過」）。 */
  keep_when_requester: boolean;
  // V352: null = 預設路線（沒有專屬設定的公司都走這組）；有值 = 該 GP 的專屬路線，
  // 整組取代預設（不疊加）。
  gp_company_id: string | null;
  gp_company_name: string | null;
}
export function useApprovalPolicies(actor: DevActorPreset, enabled: boolean) {
  return useQuery<ApprovalPolicyRow[], ApiError>({
    queryKey: ['approval-policies', actor],
    queryFn: () => api<ApprovalPolicyRow[]>('/api/v1/approval-policies', { actor }),
    enabled,
  });
}

// ---- 知會我的（V347 step_type='notify'）— 鈴鐺「📣 知會」區，近 30 天 ----
export interface NotifiedRow {
  id: string;
  request_no: string | null;
  request_type: string;
  title: string;
  status: string;
  step_label: string | null;
  notified_at: string | null;
  requester_name: string;
}
export function useNotifiedMe(actor: DevActorPreset) {
  return useQuery<NotifiedRow[], ApiError>({
    queryKey: ['approvals-notified-me', actor],
    queryFn: () => api<NotifiedRow[]>('/api/v1/approvals/notified-me', { actor }),
    refetchInterval: 60_000,
  });
}

export function useLeaveSummary(actor: DevActorPreset) {
  return useQuery<LeaveSummary, ApiError>({
    queryKey: ['leave-summary', actor],
    queryFn: () => api<LeaveSummary>('/api/v1/leave-summary', { actor }),
  });
}

// ---- 假勤管理(HR 主管):全員特休結餘 + 待審請假單 ----
export interface LeaveBalanceRow {
  user_id: string;
  name: string;
  hire_date: string | null;
  entitled: string;
  used: string;
  /** V299: true = HR 手動設過 (自動重算不覆寫); false = 系統依到職日/勞基法自動算。 */
  manual: boolean;
  /** V317: 法定特休(§38)。 未休完雇主須折算工資 —— HR 真正要盯的是這個。 */
  statutory: string;
  /** V317: 福利假。 公司額外給, 未休完依公司政策(慣例歸零)。 */
  welfare: string;
}
export interface PendingLeaveRow {
  request_id: string;
  request_no: string | null;
  requester_name: string;
  leave_type: string;
  start_date: string;
  end_date: string;
  total_days: string;
  submitted_at: string | null;
}
export function useLeaveAdminOverview(actor: DevActorPreset, year: number) {
  return useQuery<
    { year: number; balances: LeaveBalanceRow[]; pending: PendingLeaveRow[] },
    ApiError
  >({
    queryKey: ['leave-admin-overview', actor, year],
    queryFn: () => api(`/api/v1/leave-admin/overview?year=${year}`, { actor }),
  });
}

export function useLeaveTypes(actor: DevActorPreset) {
  return useQuery<LeaveType[], ApiError>({
    queryKey: ['leave-types', actor],
    queryFn: () => api<LeaveType[]>('/api/v1/leave-types', { actor }),
    staleTime: 5 * 60_000,
  });
}

// 天(小數) → 「X 天 Y 小時」顯示(hpd = 一日工時, 預設 8)。
export function fmtDayHour(days: number, hpd = 8): string {
  if (!Number.isFinite(days) || days <= 0) return '0';
  const totalH = Math.round(days * hpd * 100) / 100;
  const d = Math.floor(days + 1e-9);
  const h = Math.round((days - d) * hpd * 100) / 100;
  if (d === 0) return `${totalH} 小時`;
  if (h === 0) return `${d} 天`;
  return `${d} 天 ${h} 小時`;
}

// ---- status 顯示 ----
export const APPROVAL_STATUS: Record<ApprovalStatus, { label: string; tone: string }> = {
  draft: { label: '草稿', tone: 'bg-slate-100 text-slate-600' },
  submitted: { label: '待簽核', tone: 'bg-amber-100 text-amber-700' },
  approved: { label: '已核准 · 待付款', tone: 'bg-blue-100 text-blue-700' },
  paid: { label: '已付款', tone: 'bg-emerald-100 text-emerald-700' },
  rejected: { label: '已駁回', tone: 'bg-red-100 text-red-700' },
  returned: { label: '退回補件', tone: 'bg-orange-100 text-orange-700' },
  withdrawn: { label: '已撤回', tone: 'bg-slate-100 text-slate-500' },
  // 按的是「作廢」→ 狀態也叫已作廢。 出差例外（按「取消出差」），見 approvalStatusLabel。
  cancelled: { label: '已作廢', tone: 'bg-slate-100 text-slate-500' },
};

// 2026-08-02 使用者：「名稱的寫法能不能統一 先寫影響的主檔」——
// 一律 `主檔: 動作`。 掃過去一眼就知道這條碰的是哪一份資料，而不用讀完整句才分辨
// 「基金狀態變更」跟「專案狀態變更」差在哪。
//
// 主檔前綴四種：
//   基金 / 專案 / 叁石夥伴 / 叁石成員 —— 這條流程改的是那份主檔上的東西
//   簽核                              —— 獨立申請單，不掛在任何主檔上（報帳、請假…）
//
// ⚠「簽核:」不是廢話（整頁都是簽核）—— 它正好對上灰色徽章的意思：這類只能透過
//   申請表單發起、沒有「不簽就做」的開關。
export const REQUEST_TYPE_LABEL: Record<string, string> = {
  expense: '簽核：報帳',
  payment: '簽核：付款申請',
  leave: '簽核：請假',
  // 2026-09-06 使用者:「應該要改成 開立服務發票」—— 開立抽屜本來就叫「開立服務
  // 發票」、編輯叫「編輯服務發票申請」, 只有這條標籤還停在「顧問費」。 而這個型別
  // 早就不只開顧問費（V414 之後叁石對基金收的管理費也走它）→「服務發票」才對得上。
  advisory: '簽核：服務發票',
  // V417: 一期管理費 call 的那一批統一發票（一張單、逐位 LP 回填號碼）。
  fee_invoice: '基金：管理費開立發票',
  contract: '簽核：合約/訂閱',
  investment: '簽核：投資核決',
  // V490: 某個記帳主體跟單一股東之間的借入 / 還款。
  shareholder_loan: '簽核：關係人/股東往來',
  trip: '簽核：出差申請',
  vendor_request: '主檔：新增廠商',
  // V348 營運事件 gate（從基金/專案頁送審，不在「新增申請」picker 裡）
  capital_call: '基金：Capital Call 發放',
  distribution: '基金：分配宣告',
  // V360: 估值依標的拆兩個型別 —— 一個型別只能有一組路線，拆開才能各自設定。
  fund_valuation: '基金：估值核准',
  project_valuation: '專案：估值核准',
  // V399 / V402 投資承諾與請款（從專案頁的承諾列送審）
  commitment: '專案：投資承諾',
  commitment_disbursement: '專案：投資款請款',
  // V349 從 workflow 搬過來的 gate（批 3b 才切發起點）
  fund_status_change: '基金：狀態變更',
  project_status_change: '專案：狀態變更',
  party_status_change: '叁石夥伴：狀態變更',
  // 2026-08-01 使用者：「這邊是指 internal portal 的帳號嗎? 可以寫清楚?」—— 是。
  // 有人在**內部後台**自助註冊 → pending_review → 核准才成為可登入的叁石成員。
  // 投資人入口那邊是驗證信箱即啟用、沒有審核關（V142），兩者不同。
  // 前綴用「叁石成員」就把「哪一邊的帳號」講清楚了，不必再寫「內部後台」。
  access_request: '叁石成員：申請加入基金/專案',
  internal_signup_review: '叁石成員：帳號註冊審核',
};

/**
 * 待辦清單 / 單據頁用的短標籤 —— 只去掉「簽核: 」前綴。
 *
 * 2026-08-22 UX 稽核 #3（從新人視角看清單）：待辦表的「類型」欄每一列都寫
 * 「簽核: 付款申請」「簽核: 報帳」，而那張表**每一列本來就是待簽的單**，前綴
 * 只是把最窄的一欄推寬。
 *
 * ⚠ 但不能把前綴從 REQUEST_TYPE_LABEL 拿掉 —— 在**簽核路線設定頁**它 15 種流程
 *   排在一起，前綴是主檔分群（簽核 / 基金 / 專案 / 叁石夥伴 / 叁石成員），而且
 *   正好對上那頁的 🔒/🔓 徽章語意（見上面的說明）。 所以是「顯示端挑短的用」，
 *   不是改資料。 其餘前綴（基金: / 專案: / 叁石夥伴:）保留：那是「動的是哪份
 *   主檔」，讀清單的人需要。
 */
export function requestTypeShortLabel(type: string): string {
  return (REQUEST_TYPE_LABEL[type] ?? type).replace(/^簽核：/, '');
}

// 每種簽核類型的 icon（統一待辦清單 / picker 共用）。
export const REQUEST_TYPE_ICON: Record<string, string> = {
  expense: '📋',
  payment: '💳',
  leave: '🏖️',
  advisory: '🧾',
  fee_invoice: '🧾',
  contract: '📄',
  investment: '📈',
  shareholder_loan: '🤝',
  trip: '✈️',
  vendor_request: '🏭',
  commitment: '🤝',
  commitment_disbursement: '📤',
  capital_call: '💰',
  distribution: '💸',
  valuation: '📊',
  fund_status_change: '💼',
  project_status_change: '🚀',
  party_status_change: '🤝',
  access_request: '🔑',
  internal_signup_review: '🪪',
};

// ---- V348 gate 開關（哪些營運事件要走事前簽核）----
export function useApprovalGates(actor: DevActorPreset) {
  return useQuery<string[], ApiError>({
    queryKey: ['approval-gates', actor],
    queryFn: () => api<string[]>('/api/v1/approval-gates', { actor }),
    staleTime: 60_000,
  });
}

/** 簽核狀態 → pill（給「我的待辦」統一清單用，形狀對齊 workflowStatusPill）。 */
/**
 * **有**付款階段的型別 —— 只有這兩種的 `approved` 才是「等出納把錢匯出去」，
 * 其餘一律 approved 即終態，標「已核准 · 待付款」是說謊。
 *
 * ⚠ 這裡刻意寫成**白名單**，而且逐字對齊後端 mark-paid 的守門條件
 * （`WHERE status='approved' AND request_type IN ('expense','payment')`）——
 * 那是唯一能把 `approved` 推進到 `paid` 的路徑。 原本是反過來列「沒有付款階段的」
 * 黑名單，結果**每加一種新型別就漏一次**：
 *   · V384 加出差時發現請假也漏（那次補了 leave / trip）
 *   · 2026-08-23 D-E2E 發現 V348+V349 的 9 種營運事件 gate 全漏
 *   · 2026-08-28 走查正式環境又發現 合約 / 開票 / 投資核決 三種也漏 ——
 *     它們的單子會**永遠卡在「已核准 · 待付款」**，因為後端根本不收它們的 mark-paid。
 * 白名單的預設是安全的：新型別預設沒有付款階段，除非後端真的放行。
 */
//   · 2026-09-01 又漏一次：V402 的 commitment_disbursement 後端已放行 mark-paid
//     （它就是為了出納那一關而生的）, 這裡沒補 ⇒ 狀態標「已核准」而不是
//     「已核准 · 待付款」, 出納不知道還有錢要匯。
const PAYMENT_STAGE_TYPES: ReadonlySet<string> = new Set([
  'expense',
  'payment',
  'commitment_disbursement',
]);

/**
 * 這張單走不走「出納付款」那一關？
 *
 * 逐字對齊後端的 `PAYABLE_REQUEST_TYPES`（mark-paid 的守門條件 + 詳情端點的
 * `can_mark_paid`）。 匯出成函式是因為 ApprovalDetailPage 原本又手抄了兩份
 * `expense || payment` —— 每份都要記得補, 就等於每次都會漏一份。
 */
export function hasPaymentStage(requestType: string | null | undefined): boolean {
  return !!requestType && PAYMENT_STAGE_TYPES.has(requestType);
}

/**
 * V417: 這個型別的 `approved` 是「等財務主管去開統一發票」。
 *
 * ⚠ 只列 fee_invoice，**不列 advisory**。 fee_invoice 這個型別存在的唯一理由就是
 *   那一關開票，所以標籤永遠是對的；而 advisory 有兩種情況（V417 之前核准的舊單
 *   沒有執行關、租戶也可能沒設）→ 一律標「待開票」就會對那些單說謊，正好是上面
 *   白名單那段註解在講的錯。 服務發票要開票的訊號走待辦清單（執行關本來就會落到
 *   財務主管的收件匣）與詳情頁的時間軸。
 */
const INVOICE_STAGE_TYPES: ReadonlySet<string> = new Set(['fee_invoice']);

/**
 * 2026-09-12 使用者:「已跑完的 status 要給什麼樣子的 status 比較看得懂」。
 *
 * 「已核准」講的是**流程走到哪**, 不是世界上發生了什麼。 對營運事件那一批, 核准本身
 * 就是那件事 —— Capital Call 的單寫「已核准」, 但那張 call 當下就發出去了; 讀的人卻
 * 以為還有下一步在等誰。
 *
 * 規則: **已核准只留給還有下一步的單 (出納付款 / 財務主管開票); 沒有下一步的就寫
 * 發生了什麼。**
 *
 * ⚠ 這裡只放「核准 = 事情已生效」的型別。 報帳 / 付款 / 請款不在裡面 —— 它們核准之後
 *   真的還有一關, 那句「已核准 · 待付款」是對的 (2026-08-27 使用者問過一次)。
 */
const OUTCOME_LABEL: Readonly<Record<string, string>> = {
  capital_call: '已發放',
  distribution: '已宣告',
  fund_valuation: '已生效',
  project_valuation: '已生效',
  fund_status_change: '已變更',
  project_status_change: '已變更',
  party_status_change: '已變更',
  access_request: '已開通',
  vendor_request: '已建立',
  commitment: '已成立',
  internal_signup_review: '已開通',
};

export function approvalStatusLabel(
  status: ApprovalStatus,
  requestType?: string,
  /**
   * 這張單還卡在執行關嗎（清單端點的 `pending_execute`）。 只有 fee_invoice 用得到：
   * 它存在的唯一理由就是那一關，所以 false = 票已經開完了。
   * ⚠ 不要拿它去標 advisory 的「已開票」—— V417 之前核准的舊單根本沒有執行關，
   *   pending_execute 一樣是 false，那會對從沒開過票的單說它開完了。
   */
  pendingExecute?: boolean,
): string {
  // 出差的出口叫「取消出差」, 其餘型別叫「作廢」—— 狀態跟著按鈕講。
  if (status === 'cancelled' && requestType === 'trip') return '已取消';
  // requestType 沒傳時維持原標籤 —— 呼叫端多半是通用清單, 猜錯比不改更糟。
  if (status === 'approved' && requestType && INVOICE_STAGE_TYPES.has(requestType))
    return pendingExecute === false ? '已開票' : '待開票';
  // V490: 股東往來核准之後還有「出納記到款」一關 —— 那關做完才算錢真的進出。
  if (status === 'approved' && requestType === 'shareholder_loan')
    return pendingExecute === false ? '已記到款' : '已核准 · 待記到款';
  if (status === 'approved' && requestType && OUTCOME_LABEL[requestType])
    return OUTCOME_LABEL[requestType] as string;
  if (status === 'approved' && requestType && !PAYMENT_STAGE_TYPES.has(requestType))
    return '已核准';
  return APPROVAL_STATUS[status].label;
}
export function approvalStatusPill(
  status: ApprovalStatus,
  requestType?: string,
  pendingExecute?: boolean,
): { label: string; className: string } {
  const s = APPROVAL_STATUS[status];
  return {
    label: approvalStatusLabel(status, requestType, pendingExecute),
    className: `${s.tone} border-transparent`,
  };
}

// 顯示 fallback；權威清單來自 /leave-types(leave_type_policy)。與 V255 種子對齊。
export const LEAVE_TYPE_LABEL: Record<string, string> = {
  annual: '特別休假',
  sick: '普通傷病假',
  occupational_injury: '公傷病假',
  personal: '事假',
  family_care: '家庭照顧假',
  menstrual: '生理假',
  marriage: '婚假',
  bereavement: '喪假',
  maternity: '產假',
  miscarriage: '流產假',
  prenatal_checkup: '產檢假',
  paternity: '陪產檢及陪產假',
  official: '公假',
};
